import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtDuracion } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

const ESTADO_UNIDAD = {
  pendiente: { label: "Pendiente", className: "border-amber-200 bg-amber-50 text-amber-800" },
  aprobada: { label: "Aprobada", className: "border-green-200 bg-green-50 text-green-800" },
  rechazada: { label: "Rechazada", className: "border-red-200 bg-red-50 text-red-800" },
  pendiente_revision: { label: "Rejustificación", className: "border-violet-200 bg-violet-50 text-violet-800" },
  cancelada_trabajador: { label: "Cancelada", className: "border-zinc-200 bg-zinc-50 text-zinc-600" },
};

export default function RevisionAusenciaJustificadaDialog({ solicitud, open, onOpenChange, onSuccess }) {
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [comentario, setComentario] = useState("");
  const [unidades, setUnidades] = useState([]);
  const [decisiones, setDecisiones] = useState({});

  useEffect(() => {
    if (!open || !solicitud?.id) return;
    setCargando(true);
    setComentario("");
    api
      .get(`/solicitudes/${solicitud.id}`)
      .then((r) => {
        const u = r.data.ausencia_unidades || [];
        setUnidades(u);
        const ini = {};
        u.forEach((item) => {
          if (item.estado === "pendiente" || item.estado === "pendiente_revision") {
            ini[item.id] = "aprobar";
          }
        });
        setDecisiones(ini);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setCargando(false));
  }, [open, solicitud?.id]);

  const pendientes = useMemo(
    () => unidades.filter((u) => u.estado === "pendiente" || u.estado === "pendiente_revision"),
    [unidades],
  );

  const actualizar = (id, accion) => setDecisiones((d) => ({ ...d, [id]: accion }));

  const aprobarTodo = () => {
    const d = {};
    pendientes.forEach((u) => {
      d[u.id] = "aprobar";
    });
    setDecisiones(d);
  };

  const rechazarTodo = () => {
    const d = {};
    pendientes.forEach((u) => {
      d[u.id] = "rechazar";
    });
    setDecisiones(d);
  };

  const guardar = async () => {
    if (!pendientes.length) {
      toast.error("No hay tramos pendientes de revisión");
      return;
    }
    const sinDecision = pendientes.filter((u) => !decisiones[u.id]);
    if (sinDecision.length) {
      toast.error("Indica una decisión para cada tramo");
      return;
    }
    setGuardando(true);
    try {
      await api.put(`/solicitudes/${solicitud.id}/revisar`, {
        accion: "aprobar",
        comentario,
        unidades: pendientes.map((u) => ({
          id: u.id,
          accion: decisiones[u.id],
        })),
      });
      toast.success("Ausencia justificada revisada");
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="revision-ausencia-dialog">
        <DialogHeader>
          <DialogTitle>Revisar ausencia justificada</DialogTitle>
        </DialogHeader>
        {solicitud && (
          <p className="text-sm text-zinc-500">
            {solicitud.usuario} · {solicitud.motivo}
          </p>
        )}
        {cargando ? (
          <p className="text-sm text-zinc-500">Cargando tramos…</p>
        ) : (
          <>
            <p className="text-xs text-zinc-500">
              Aprueba o rechaza cada tramo. Los tramos rechazados se devuelven al trabajador para que justifique o cancele.
            </p>
            {pendientes.length > 1 && (
              <div className="flex gap-2">
                <Button type="button" size="sm" variant="outline" onClick={aprobarTodo}>
                  Aprobar todo
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={rechazarTodo}>
                  Rechazar todo
                </Button>
              </div>
            )}
            <div className="max-h-[40vh] space-y-2 overflow-y-auto pr-1">
              {unidades.map((u) => {
                const st = ESTADO_UNIDAD[u.estado] || ESTADO_UNIDAD.pendiente;
                const editable = u.estado === "pendiente" || u.estado === "pendiente_revision";
                return (
                  <div key={u.id} className="rounded-xl border border-zinc-200 bg-zinc-50/80 p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-sm font-medium text-zinc-900">{u.label}</span>
                      <div className="flex items-center gap-2">
                        {fmtDuracion(u.segundos) && (
                          <span className="text-xs font-medium text-zinc-600">{fmtDuracion(u.segundos)}</span>
                        )}
                        {!editable && (
                          <Badge variant="outline" className={st.className}>
                            {st.label}
                          </Badge>
                        )}
                      </div>
                    </div>
                    {u.fin_si_no_vuelve && u.fin_si_no_vuelve !== u.franja_fin && (
                      <p className="mt-1 text-xs text-zinc-500">Si no vuelve, cierre previsto: {u.fin_si_no_vuelve}</p>
                    )}
                    {u.motivo_trabajador && (
                      <p className="mt-1 text-xs text-violet-700">Trabajador: {u.motivo_trabajador}</p>
                    )}
                    {editable && (
                      <Select value={decisiones[u.id] || ""} onValueChange={(v) => actualizar(u.id, v)}>
                        <SelectTrigger className="mt-2 h-9 bg-white">
                          <SelectValue placeholder="Decisión…" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="aprobar">Aprobar</SelectItem>
                          <SelectItem value="rechazar">Rechazar</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                );
              })}
            </div>
            <Textarea
              placeholder="Comentario para el trabajador (opcional)"
              value={comentario}
              onChange={(e) => setComentario(e.target.value)}
            />
          </>
        )}
        <DialogFooter>
          <Button onClick={guardar} disabled={guardando || cargando || !pendientes.length} className="bg-indigo-600 hover:bg-indigo-700">
            {guardando ? "Guardando…" : "Confirmar revisión"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
